/**
 * VÉRAULT Portal — Client: Travelers
 * Quiet roster of the people in the household traveling.
 */
import React from "react";
import { motion } from "framer-motion";
import { Users } from "lucide-react";
import {
  Surface,
  PageHeader,
  EditorialRule,
  KeyValue,
  Empty,
  Skeleton,
  api,
  fmt,
  motion as portalMotion,
} from "../../index";

const { fadeInUp, stagger } = portalMotion;

export default function ClientTravelers() {
  const [travelers, setTravelers] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    api
      .get("/client-portal/travelers")
      .then(({ data }) => alive && setTravelers(Array.isArray(data) ? data : []))
      .catch(() => alive && setError("Couldn't load your travelers."));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-10">
      <motion.div variants={fadeInUp}>
        <PageHeader
          eyebrow="Travelers"
          title="Who's coming along."
          lede="The people in your household — and the details that keep things smooth."
        />
      </motion.div>

      <motion.div variants={fadeInUp}>
        <EditorialRule label={travelers ? `${travelers.length} on the manifest` : "loading"} />
        <div className="mt-6">
          {error ? (
            <Empty title={error} icon={<Users className="size-6" strokeWidth={1.25} />} />
          ) : !travelers ? (
            <div className="grid sm:grid-cols-2 gap-5">
              {[0, 1].map((i) => <Skeleton key={i} className="h-40" />)}
            </div>
          ) : travelers.length === 0 ? (
            <Empty
              title="No travelers on file yet"
              hint="Your advisor will add household members as they're confirmed for the experience."
              icon={<Users className="size-6" strokeWidth={1.25} />}
            />
          ) : (
            <ul className="grid sm:grid-cols-2 gap-5" data-testid="travelers-grid">
              {travelers.map((t) => (
                <li key={t.id}>
                  <Surface variant="default" padded className="!p-5" data-testid={`traveler-${t.id}`}>
                    <p className="font-serif text-xl text-[hsl(var(--foreground))]">
                      {t.full_name || `${t.first_name || ""} ${t.last_name || ""}`.trim() || "Unnamed"}
                    </p>
                    {t.nationality && (
                      <p className="mt-0.5 font-accent italic text-[12.5px] text-[hsl(var(--foreground-secondary))]">
                        {t.nationality}
                      </p>
                    )}
                    <div className="mt-4 grid grid-cols-2 gap-4">
                      <KeyValue label="Passport" value={t.passport_number ? "On file" : null} />
                      <KeyValue label="Date of birth" value={t.date_of_birth ? fmt.formatDate(t.date_of_birth) : null} />
                      <KeyValue label="Dietary" value={t.dietary_needs} />
                      <KeyValue label="Phone" value={t.phone} />
                    </div>
                  </Surface>
                </li>
              ))}
            </ul>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
